import {Ascii} from '../../../shared/shared-models/global/ascii.enum';
import {INotification} from '../models/notification';
import {
  IQuoteNotificationFailureAction,
  IQuoteNotificationItemError,
  IQuoteNotificationItemErrorEntry,
  IQuoteNotificationItemErrorGuidance,
  QuoteNotificationFailureActionEnum,
  QuoteNotificationItemErrorReasonEnum,
} from '../models/notification-quote-item-error.model';

const QUOTE_ITEM_ERROR_GUIDANCE: Record<string, string> = {
  [QuoteNotificationItemErrorReasonEnum.itemNotFound]:
    'Remove these items — they are no longer in the stock file.',
  [QuoteNotificationItemErrorReasonEnum.iqProcessingError]:
    'Check these items in IQ, then remove or fix and retry.',
  [QuoteNotificationItemErrorReasonEnum.unknownError]:
    'Remove affected items and retry, or contact support.',
};

/**
 * Returns user-facing guidance for a quote item error reason.
 */
export function getQuoteItemErrorGuidance(
  reason: QuoteNotificationItemErrorReasonEnum | string | undefined,
): string {
  if (!reason) {
    return QUOTE_ITEM_ERROR_GUIDANCE[QuoteNotificationItemErrorReasonEnum.unknownError];
  }
  return QUOTE_ITEM_ERROR_GUIDANCE[reason]
    ?? QUOTE_ITEM_ERROR_GUIDANCE[QuoteNotificationItemErrorReasonEnum.unknownError];
}

/**
 * Whether the notification is a failed quote response.
 */
export function isQuoteNotificationFailure(notification: INotification | undefined): boolean {
  return !!notification && notification.type === 'QUOTES' && notification.success === false;
}

/**
 * Resolves itemErrors from the quote notification document.
 */
export function resolveQuoteNotificationItemErrors(
  notification: INotification | undefined,
): Record<string, IQuoteNotificationItemError> | null {
  const rawErrors = notification?.itemErrors;
  if (!rawErrors || Object.keys(rawErrors).length === 0) {
    return null;
  }

  return Object.fromEntries(
    Object.entries(rawErrors).map(([stockCode, itemError]) => [
      stockCode,
      {
        desc: itemError.desc,
        error: itemError.error,
        reason: itemError.reason
          || QuoteNotificationItemErrorReasonEnum.unknownError,
      },
    ]),
  );
}

/**
 * Builds display rows for each failing stock item on a quote notification.
 */
export function buildQuoteNotificationItemErrorEntries(
  itemErrors: Record<string, IQuoteNotificationItemError> | null,
): IQuoteNotificationItemErrorEntry[] {
  if (!itemErrors) {
    return [];
  }

  return Object.entries(itemErrors).map(([stockCode, itemError]: [string, IQuoteNotificationItemError]) => ({
    stockCode,
    desc: itemError.desc,
    reason: itemError.reason,
    guidance: getQuoteItemErrorGuidance(itemError.reason),
  }));
}

/**
 * Returns unique guidance messages for failing quote items grouped by reason.
 */
export function buildQuoteNotificationItemErrorGuidance(
  entries: IQuoteNotificationItemErrorEntry[],
): IQuoteNotificationItemErrorGuidance[] {
  const seenReasons = new Set<string>();
  const guidance: IQuoteNotificationItemErrorGuidance[] = [];

  entries.forEach((entry: IQuoteNotificationItemErrorEntry) => {
    const reasonKey: string = entry.reason || QuoteNotificationItemErrorReasonEnum.unknownError;
    if (seenReasons.has(reasonKey)) {
      return;
    }
    seenReasons.add(reasonKey);
    guidance.push({
      reason: entry.reason,
      guidance: entry.guidance,
    });
  });

  return guidance;
}

/**
 * Builds modal description lines for a failed quote notification.
 */
export function buildQuoteNotificationErrorDescription(
  notification: INotification | undefined,
): string[] {
  const lines: string[] = [];
  const summary: string | undefined = notification?.response;

  if (summary) {
    lines.push(summary);
    lines.push(Ascii.EmptySpace);
  }

  const entries: IQuoteNotificationItemErrorEntry[] = buildQuoteNotificationItemErrorEntries(
    resolveQuoteNotificationItemErrors(notification),
  );

  if (entries.length > 0) {
    lines.push('The following items could not be processed:');
    lines.push(Ascii.EmptySpace);
    entries.forEach((entry: IQuoteNotificationItemErrorEntry) => {
      lines.push(`${entry.stockCode} — ${entry.desc}`);
    });
    lines.push(Ascii.EmptySpace);
    buildQuoteNotificationItemErrorGuidance(entries).forEach(
      (guidanceEntry: IQuoteNotificationItemErrorGuidance) => {
        lines.push(guidanceEntry.guidance);
        lines.push(Ascii.EmptySpace);
      },
    );
  }

  if (lines.length === 0) {
    lines.push('Quote processing failed. Please contact support.');
  }

  return lines;
}

/**
 * Resolves available failure actions for a quote notification.
 * Extend this as retry and remediations are added.
 */
export function resolveQuoteNotificationFailureActions(
  notification: INotification | undefined,
): IQuoteNotificationFailureAction[] {
  if (!isQuoteNotificationFailure(notification)) {
    return [];
  }

  const actions: IQuoteNotificationFailureAction[] = [];

  if (notification.response || resolveQuoteNotificationItemErrors(notification)) {
    actions.push({
      action: QuoteNotificationFailureActionEnum.viewError,
      label: 'View error',
    });
  }

  return actions;
}
